import React from 'react';
import { NavLink } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { NAV } from '@/constants/testIds';
import { BrandLockup } from '@/components/brand/BrandMark';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { adminNav, adminMobileTabs, cleanerTabs } from './navConfig';

// Shared by the desktop sidebar and the mobile drawer so both render identically.
function NavGroups({ onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
      {adminNav.map(group => (
        <div key={group.group}>
          <div className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-white/40">{group.group}</div>
          <div className="space-y-0.5">
            {group.items.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `relative flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-white/10 text-white font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-[hsl(var(--fc-sidebar-active))]'
                      : 'hover:bg-white/5 hover:text-white'
                  }`
                }
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function SidebarNav() {
  return (
    <aside
      data-testid={NAV.sidebar}
      className="hidden lg:flex lg:fixed lg:inset-y-0 lg:z-30 lg:w-[264px] lg:flex-col bg-[hsl(var(--fc-sidebar))] text-[hsl(var(--fc-sidebar-foreground))]"
    >
      <div className="flex h-14 items-center border-b border-white/10 px-5" data-testid={NAV.logo}>
        <BrandLockup tone="light" />
      </div>
      <NavGroups />
    </aside>
  );
}

// The phone equivalent of the sidebar. Below `lg` the sidebar is hidden entirely,
// so without this an admin on a phone has no way to reach 12 of the 16 screens.
export function MobileNavDrawer({ open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        data-testid="mobile-nav-drawer"
        className="w-[280px] p-0 flex flex-col border-r-0 bg-[hsl(var(--fc-sidebar))] text-[hsl(var(--fc-sidebar-foreground))] [&>button]:text-white/70 [&>button]:hover:text-white"
      >
        <div className="flex h-14 shrink-0 items-center border-b border-white/10 px-5">
          <BrandLockup tone="light" />
        </div>
        <NavGroups onNavigate={() => onOpenChange(false)} />
      </SheetContent>
    </Sheet>
  );
}

// `hideOnDesktop` is only for admins — the cleaner shell has no sidebar at any
// width, so its tab bar has to stay visible on desktop too.
function TabBar({ children, hideOnDesktop }) {
  return (
    <nav className={`${hideOnDesktop ? 'lg:hidden ' : ''}fixed bottom-0 left-0 right-0 z-40 border-t bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)]`}>
      <div className="flex">{children}</div>
    </nav>
  );
}

const tabClass = ({ isActive }) =>
  `flex-1 flex flex-col items-center justify-center gap-1 min-h-[56px] py-2 text-[11px] font-medium transition-colors ${
    isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
  }`;

export function AdminBottomBar({ onMore }) {
  return (
    <TabBar hideOnDesktop>
      {adminMobileTabs.map(t => (
        <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid} className={tabClass}>
          <t.icon className="h-5 w-5" />
          <span className="leading-none">{t.label}</span>
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onMore}
        data-testid="mobile-tab-more"
        className="flex-1 flex flex-col items-center justify-center gap-1 min-h-[56px] py-2 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <MoreHorizontal className="h-5 w-5" />
        <span className="leading-none">More</span>
      </button>
    </TabBar>
  );
}

export function CleanerBottomBar() {
  return (
    <TabBar>
      {cleanerTabs.map(t => (
        <NavLink key={t.to} to={t.to} data-testid={t.testid} className={tabClass}>
          <t.icon className="h-5 w-5" />
          <span className="leading-none">{t.label}</span>
        </NavLink>
      ))}
    </TabBar>
  );
}
